'use client';

import { useMemo } from 'react';
import {
  createEbvValueFormatter,
  EBV_SORT_ORDER,
  ebvGroupingOptionsFromDisplay,
  getEbvCategoryRanker,
  compareEbvByCategoryOrder,
  getEbvDisplayCategory,
  parseEbvAverage,
  parseEbvConfig,
} from '@/hooks/publicSearch/ebv/ebvColumns';
import { useEbvDisplayOptions } from '@/hooks/publicSearch/useEbvDisplayOptions';
import {
  coerceBreedAverageRaw,
  formatBreedAverageDisplay,
} from '@/components/publicSearch/animal/ebv/breedAverageDisplay';
import { resolveGraphFooterCaption } from '@/components/publicSearch/animal/ebv/breedAverageLabels';
import type { EbvChartItem } from '@/components/publicSearch/animal/ebv/EbvPercentileGraph';
import { getEbvConfigTraitKey } from '@/components/publicSearch/animal/ebv/ebvTraitExplanations';
import type { Society } from '@/publicSearch/store/usePublicSearchContext';
import { useCurrencySymbol } from '@/hooks/publicSearch/useCurrencySymbol';
import type { ProjectedEbv } from '@/components/publicSearch/mating/types';

/**
 * Builds EBV percentile chart rows for expected progeny (midparent EBV ranked against
 * society percentile bands). Percentiles come from the mating predictor API — not from
 * averaging parent percentiles.
 */
export function useProjectedProgenyEbvChartData(
  society: Society,
  projectedEbvs: ProjectedEbv[] | undefined,
) {
  const ebvConfig = useMemo(
    () => parseEbvConfig(society.settings),
    [society.settings],
  );

  const currencySymbol = useCurrencySymbol(society);
  const { useColourSlotGrouping } = useEbvDisplayOptions();
  const ebvGroupingOptions = useMemo(
    () => ebvGroupingOptionsFromDisplay({ useColourSlotGrouping }),
    [useColourSlotGrouping],
  );

  const ebvAverageData = useMemo(
    () => parseEbvAverage(society.settings),
    [society.settings],
  );

  const projectedByNumber = useMemo(() => {
    const map = new Map<number, ProjectedEbv>();
    for (const item of projectedEbvs ?? []) {
      map.set(item.ebvNumber, item);
    }
    return map;
  }, [projectedEbvs]);

  const chartData = useMemo((): EbvChartItem[] => {
    const visible = ebvConfig.filter(
      (ebvField) =>
        ebvField &&
        (ebvField.display === 1 || ebvField.display === 2) &&
        ebvField.field?.startsWith('ebv') &&
        ebvField.label,
    );

    const formatValue = (value: number | null | undefined, format: string) => {
      if (value == null) return '';
      return createEbvValueFormatter(format, currencySymbol)(String(value));
    };

    const items = visible.map((ebvField) => {
      const fieldNumber = ebvField.field.replace('ebv', '');
      const fieldIndex = Number(fieldNumber);
      const avgKey = `ebv_${fieldIndex}_avg`;
      const seKey = `ebv_${fieldIndex}_se`;
      const projected = projectedByNumber.get(fieldIndex);

      return {
        field: ebvField.field,
        label: ebvField.label.replace(/\^/g, ' '),
        leftLabel: ebvField.leftLabel || ebvField.label.replace(/\^/g, ' '),
        rightLabel: ebvField.rightLabel || '',
        value: formatValue(projected?.value, ebvField.format || '0'),
        accuracy: projected?.accuracy ?? '',
        percentile: projected?.percentile ?? null,
        breedAvg:
          ebvAverageData && ebvAverageData[avgKey] !== undefined
            ? String(ebvAverageData[avgKey] ?? '')
            : '',
        formattedBreedAvg:
          ebvAverageData && ebvAverageData[avgKey] !== undefined
            ? formatBreedAverageDisplay(
                coerceBreedAverageRaw(ebvAverageData[avgKey]),
                ebvField.format || '0',
                currencySymbol,
              )
            : '',
        colour: ebvField.colour || 'B',
        chartColour: ebvField.chartColour,
        sortOrder: ebvField.sortOrder || EBV_SORT_ORDER.DESC,
        standardError:
          ebvAverageData && ebvAverageData[seKey] !== undefined
            ? String(ebvAverageData[seKey] ?? '')
            : '',
        lowValue: ebvField.lowValue || '',
        topValue: ebvField.topValue || '',
        category: getEbvDisplayCategory(ebvField, ebvGroupingOptions),
        categoryOrder: ebvField.categoryOrder,
        traitKey: getEbvConfigTraitKey(ebvField),
      };
    });

    const rankCategory = getEbvCategoryRanker(ebvConfig, ebvGroupingOptions);
    return items.sort((a, b) =>
      compareEbvByCategoryOrder(
        a.category,
        a.categoryOrder,
        b.category,
        b.categoryOrder,
        rankCategory,
      ),
    );
  }, [
    ebvConfig,
    ebvAverageData,
    projectedByNumber,
    currencySymbol,
    ebvGroupingOptions,
  ]);

  return {
    chartData,
    breedAverageMessage: resolveGraphFooterCaption(ebvAverageData) ?? '',
    hasPercentileData: (projectedEbvs ?? []).some(
      (item) => item.percentile != null,
    ),
  };
}
